import React, { useState, useEffect } from "react";
import Pagination from "./Pagination";
import SearchBar from "./SearchBar/SearchBar";
import { BiSolidDownload } from "react-icons/bi";
import { FaCircle, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import Avatar from "react-avatar";
// debounce removed (not used) to keep filtering in a single effect
import { ToastContainer, toast } from "react-toastify";
import "./Datatable.css";

export const Datatable = ({
  columns = [],
  TableData = [],
  isDoubleClick,
  sortConfig,
  handleSort,
  myClass,
  pagination,
  generatePDFs,
  refe,
  generatePDFCondition,
  isSearchBar,
  isRetrieved = true,
  onPageChange = () => {},
  headingDataOnTable,
  TableButton,
}) => {
  const [internalSort, setInternalSort] = useState(null);
  const [statusFilterLocal, setStatusFilterLocal] = useState('all');
  const [filterData, setFilterData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // removed debounced helper to centralize filtering in single effect

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(filterData)
    ? filterData.slice(indexOfFirstItem, indexOfLastItem)
    : [];
  const totalPages = Math.ceil((filterData?.length || 0) / itemsPerPage);

  const handleCheckAll = () => {
    if (Array.isArray(TableData)) {
      setSelectedRows(isCheckAll ? [] : TableData.map((row) => row.id));
      setIsCheckAll(!isCheckAll);
    }
  };

  const getSortIcon = (key) => {
    const cfg = internalSort || sortConfig;
    if (!cfg || cfg.key !== key) return <FaSort className="ms-1 text-gray-500" />;
    return cfg.direction === "ascending" ? (
      <FaSortUp className="ms-1 text-gray-500" />
    ) : (
      <FaSortDown className="ms-1 text-gray-500" />
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // internal sort handler when parent doesn't provide one
  const onHeaderSort = (key) => {
    if (!key) return;
    // prevent sorting on actions or checkbox
    if (key === 'checkbox' || key === 'actions') return;
    let direction = 'ascending';
    if (internalSort && internalSort.key === key) {
      direction = internalSort.direction === 'ascending' ? 'descending' : 'ascending';
    }
    const cfg = { key, direction };
    setInternalSort(cfg);
    if (handleSort) handleSort(cfg);
    // apply sort immediately to filtered data
    setFilterData((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      arr.sort((a, b) => {
        const va = a?.[key] ?? '';
        const vb = b?.[key] ?? '';
        // try numbers first
        const na = parseFloat(va);
        const nb = parseFloat(vb);
        if (!isNaN(na) && !isNaN(nb)) {
          return direction === 'ascending' ? na - nb : nb - na;
        }
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        if (sa < sb) return direction === 'ascending' ? -1 : 1;
        if (sa > sb) return direction === 'ascending' ? 1 : -1;
        return 0;
      });
      return arr;
    });
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((row) => row !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const original = Array.isArray(TableData) ? TableData : [];
    let filtered = original;
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = original.filter((item) =>
        item &&
        Object.values(item).some((val) =>
          val?.toString().toLowerCase().includes(lowerQuery)
        )
      );
    }

    // apply status filter if present (be tolerant to casing/keys/whitespace)
    if (statusFilterLocal && statusFilterLocal !== 'all') {
      filtered = filtered.filter((item) => {
        if (!item) return false;
        // prefer common status keys
        const raw = item.status ?? item.booking_status ?? item.status_text ?? '';
        const statusVal = String(raw).trim().toLowerCase();
        if (statusVal) return statusVal === statusFilterLocal;
        // fallback: try to find a matching value among likely fields
        const possible = ['status', 'booking_status', 'status_text', 'state'];
        for (const k of possible) {
          if (item[k]) {
            if (String(item[k]).trim().toLowerCase() === statusFilterLocal) return true;
          }
        }
        return false;
      });
    }

    // apply internal sort if present
    const cfg = internalSort || sortConfig;
    if (cfg && cfg.key) {
      filtered = [...filtered].sort((a, b) => {
        const va = a?.[cfg.key] ?? '';
        const vb = b?.[cfg.key] ?? '';
        const na = parseFloat(va);
        const nb = parseFloat(vb);
        if (!isNaN(na) && !isNaN(nb)) {
          return cfg.direction === 'ascending' ? na - nb : nb - na;
        }
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        if (sa < sb) return cfg.direction === 'ascending' ? -1 : 1;
        if (sa > sb) return cfg.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    setFilterData(filtered);
  }, [searchQuery, TableData, internalSort, sortConfig, statusFilterLocal]);

  const showTable = currentItems.length > 0 && isRetrieved;

  return (
    <div className="table-container">
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="table-header d-flex justify-content-between align-items-center flex-wrap">
        <div className="heading-title">{headingDataOnTable && headingDataOnTable}</div>

        <div className="table-actions d-flex align-items-center gap-2">
            <div>
              <select
                className="form-select form-select-sm"
                value={statusFilterLocal}
                onChange={(e) => { setStatusFilterLocal(e.target.value); setCurrentPage(1); }}
                style={{ marginRight: 8 }}
              >
                <option value="all">All</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {isSearchBar && (
              <SearchBar setSearchQuery={setSearchQuery} searchQuery={searchQuery} />
            )}

          {TableButton}

          {generatePDFCondition && (
            <button className="btn btn-download" onClick={generatePDFs}>
              <BiSolidDownload className="icon" />
              <span className="btn-text">Download PDF</span>
            </button>
          )}
        </div>
      </div>

      {!showTable ? (
        <div className="no-records">
          <p>No records found.</p>
        </div>
      ) : (
        <div className="table-wrapper table">
          <table className={`data-table ${myClass ? "hover " : ""}`} ref={refe}>
            <thead>
              <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      onClick={() => onHeaderSort(col.key)}
                      style={col.width ? { width: col.width } : undefined}
                    >
                      {col.key === "checkbox" ? (
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={
                            selectedRows.length === TableData.length &&
                            TableData.length > 0
                          }
                          onChange={handleCheckAll}
                        />
                      ) : (
                        <>
                          {col.label}
                          {getSortIcon(col.key)}
                        </>
                      )}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: currentItems.length || 5 }).map((_, index) => (
                    <tr key={index} className="skeleton-row">
                      {columns.map((_, i) => (
                        <td key={i}>
                          <div className="skeleton-box"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                : currentItems.map((row, indx) => (
                    <tr
                      key={indx}
                      className={selectedRows.includes(row.id) ? "selected" : ""}
                      {...(isDoubleClick && {
                        onDoubleClick: () => isDoubleClick(row),
                      })}
                    >
                      {columns.map((col, colIndex) => (
                        <td key={colIndex}>
                          {col.render ? (
                            col.render(row)
                          ) : col.key === "checkbox" ? (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedRows.includes(row.id)}
                              onChange={() => handleRowSelect(row.id)}
                            />
                          ) : col.key === "status" ? (
                            <span
                              className={`status-badge status-${row[col.key]?.toLowerCase()}`}
                            >
                              <FaCircle className="status-icon" />
                              {row[col.key]}
                            </span>
                          ) : col.key === "name" ? (
                            <div className="name-cell">
                              <Avatar
                                name={row[col.key]}
                                size="32"
                                round={true}
                                className="avatar"
                              />
                              <span className="name-text">{row[col.key]}</span>
                            </div>
                          ) : (
                            <span>{row[col.key]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && filterData.length > 0 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setItemsPerPage={setItemsPerPage}
          totalPages={totalPages}
          totalItems={filterData.length}
        />
      )}
    </div>
  );
};

export default Datatable;
